<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import InputLabel from '@/js/components/InputLabel.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { PlusIcon, CheckIcon } from '@lucide/vue';
    import {
        useQueryGetSysOpUsers,
        useMutationCreateSysOpUser,
        useMutationChangeUserRole,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    // Must be kept in sync with RoleEntity.SysOpId / RoleEntity.MemberId in
    // backend/src/Echo.DataAccessLayer/Entities/Echo/RoleEntity.cs.
    const SYSOP_ROLE_ID = '13371337-1337-1337-1337-133713371337';
    const MEMBER_ROLE_ID = 'abc123ab-c123-abc1-23ab-c123abc123ab';

    // New SysOp users only ever go into this one tenant (same on local/dev/prod). Must be kept in
    // sync with CustomerEntity.SysOpTenantId in backend/src/Echo.DataAccessLayer/Entities/Master/CustomerEntity.cs
    // — the backend enforces this independently, so a mismatch here just shows an error rather than being a security gap.
    const SYSOP_TENANT_ID = 'ccb49594-7cc6-45a5-9327-fb0e2066be5e';

    const successDialogStore = useSuccessDialog();

    const { data: sysOpUsersData, isLoading } = useQueryGetSysOpUsers();

    type Row = {
        tenantId: string;
        tenantName: string;
        userId: string;
        email: string;
        name: string;
        roleName: string;
        isSysOp: boolean;
        emailVerified: boolean;
    };

    const rows = computed<Row[]>(() => {
        const entries = sysOpUsersData.value ?? {};
        return Object.entries(entries).flatMap(([tenantName, entry]) =>
            entry.users.map(({ user, emailVerified }) => ({
                tenantId: entry.tenantId,
                tenantName,
                userId: user.id,
                email: user.email ?? '',
                name: [user.firstName, user.lastName].filter(Boolean).join(' '),
                roleName: user.role?.name ?? '',
                isSysOp: user.role?.id === SYSOP_ROLE_ID,
                emailVerified,
            }))
        );
    });

    const { mutateAsync: changeUserRoleMutation, isPending: isChangingRole } =
        useMutationChangeUserRole();

    // Toggling off always demotes to MEMBER_ROLE_ID, with no way to restore the row's prior role. Only
    // safe for a verified row already in SYSOP_TENANT_ID, so the template disables the checkbox for any
    // other row (a client-tenant Admin shown here only for their email, or a not-yet-verified new user).
    const toggleSysOp = async (row: Row) => {
        try {
            await changeUserRoleMutation({
                tenantId: row.tenantId,
                userId: row.userId,
                roleId: row.isSysOp ? MEMBER_ROLE_ID : SYSOP_ROLE_ID,
            });
        } catch {
            // Error displayed via the mutation's onError handler (showSysOpUserError) in backOfficeQueries.ts
        }
    };

    // Add user: always into SYSOP_TENANT_ID with the Member role. A brand-new identity starts unverified,
    // and SysOp can only be granted to an already-verified @indicateme.se identity, so it only becomes
    // reachable afterward, via the table toggle.
    const showAddUserForm = ref(false);
    const addUserEmail = ref('');
    const addUserFirstName = ref('');
    const addUserLastName = ref('');

    const canCreateUser = computed(
        () =>
            !!addUserEmail.value.trim() &&
            !!addUserFirstName.value.trim() &&
            !!addUserLastName.value.trim()
    );

    const { mutateAsync: createSysOpUserMutation, isPending: isCreatingUser } =
        useMutationCreateSysOpUser();

    const submitAddUser = async () => {
        if (!canCreateUser.value) return;
        try {
            await createSysOpUserMutation({
                tenantId: SYSOP_TENANT_ID,
                email: addUserEmail.value.trim(),
                firstName: addUserFirstName.value.trim(),
                lastName: addUserLastName.value.trim(),
                roleId: MEMBER_ROLE_ID,
            });
        } catch {
            // Error displayed via the mutation's onError handler (showSysOpUserError) in backOfficeQueries.ts
            return;
        }
        addUserEmail.value = '';
        addUserFirstName.value = '';
        addUserLastName.value = '';
        showAddUserForm.value = false;
        successDialogStore.openDialog({ message: __('back-office-sysop-users-add-user-success') });
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-sysop-users') }}</h1>
        </template>
        <template #content>
            <div class="flex flex-wrap items-end gap-3 mb-6">
                <EchoButton variant="primary" @click="showAddUserForm = !showAddUserForm">
                    {{ __('back-office-sysop-users-add-user') }}
                    <template #icon-right><PlusIcon /></template>
                </EchoButton>
            </div>

            <form
                v-if="showAddUserForm"
                class="w-lg max-w-full mb-8"
                @submit.prevent="submitAddUser"
            >
                <FormGroup>
                    <div>
                        <InputLabel for="sysop-users-add-email">{{ __('email') }}</InputLabel>
                        <TextInput
                            id="sysop-users-add-email"
                            v-model="addUserEmail"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                    <div>
                        <InputLabel for="sysop-users-add-first-name">
                            {{ __('user-first-name') }}
                        </InputLabel>
                        <TextInput
                            id="sysop-users-add-first-name"
                            v-model="addUserFirstName"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                    <div>
                        <InputLabel for="sysop-users-add-last-name">
                            {{ __('user-last-name') }}
                        </InputLabel>
                        <TextInput
                            id="sysop-users-add-last-name"
                            v-model="addUserLastName"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                </FormGroup>
                <div class="flex gap-3 mt-6">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canCreateUser"
                        :loading="isCreatingUser"
                    >
                        {{ __('save') }}
                        <template #icon-right><CheckIcon /></template>
                    </EchoButton>
                </div>
            </form>

            <p class="mb-4 text-caption text-gray-medium">
                {{ __('back-office-sysop-users-count', { count: String(rows.length) }) }}
            </p>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="border-b border-gray-lightest">
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('back-office-users-col-tenant') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('users-table-column-name') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('email') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('back-office-users-col-role') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('verified') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('back-office-users-col-sysop') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoading">
                            <td colspan="6" class="py-12 text-center"><LoadingDots /></td>
                        </tr>
                        <template v-else>
                            <tr v-if="!rows.length">
                                <td colspan="6" class="py-6 text-body text-gray-medium text-center">
                                    {{ __('back-office-sysop-users-empty') }}
                                </td>
                            </tr>
                            <tr
                                v-for="row in rows"
                                :key="row.userId"
                                class="border-b border-gray-lightest"
                            >
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.tenantName }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.name }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.email }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.roleName }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">
                                    {{ row.emailVerified ? __('yes') : __('no') }}
                                </td>
                                <td class="py-3 pl-3 pr-6">
                                    <input
                                        type="checkbox"
                                        class="Custom-checkbox cursor-pointer"
                                        :checked="row.isSysOp"
                                        :disabled="
                                            isChangingRole ||
                                            row.tenantId !== SYSOP_TENANT_ID ||
                                            !row.emailVerified
                                        "
                                        @change="toggleSysOp(row)"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
    </SettingsPageLayout>
</template>
